const REPORTER_GPS_CACHE_KEY = 'disasterms.reporter-gps';
const REPORTER_GPS_CACHE_MS = 2 * 60 * 1000;
const REPORTER_GPS_TIMEOUT_MS = 12 * 1000;

const readCachedPosition = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(REPORTER_GPS_CACHE_KEY));
    const capturedAt = Date.parse(cached?.captured_at || '');
    if (
      Number.isFinite(cached?.lat) &&
      Number.isFinite(cached?.lng) &&
      Number.isFinite(capturedAt) &&
      Date.now() - capturedAt <= REPORTER_GPS_CACHE_MS
    ) {
      return cached;
    }
  } catch {
    // Ignore unavailable or malformed browser storage.
  }
  return null;
};

const cachePosition = (position) => {
  try {
    sessionStorage.setItem(REPORTER_GPS_CACHE_KEY, JSON.stringify(position));
  } catch {
    // Location capture must still work when browser storage is unavailable.
  }
};

const readBrowserPosition = () =>
  new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      callback(value);
    };

    const timeoutId = window.setTimeout(() => {
      const timeoutError = new Error('Browser geolocation timed out');
      timeoutError.reason = 'timeout';
      finish(reject, timeoutError);
    }, REPORTER_GPS_TIMEOUT_MS);

    watchId = navigator.geolocation.watchPosition(
      (position) => finish(resolve, position),
      (error) => finish(reject, error),
      {
        enableHighAccuracy: false,
        maximumAge: REPORTER_GPS_CACHE_MS,
        timeout: REPORTER_GPS_TIMEOUT_MS,
      }
    );
  });

export const captureReporterGeolocation = async () => {
  if (typeof navigator === 'undefined' || !("geolocation" in navigator)) {
    const unsupported = new Error('Browser geolocation is unavailable');
    unsupported.reason = 'unsupported';
    throw unsupported;
  }

  const cachedPosition = readCachedPosition();
  if (cachedPosition) return cachedPosition;

  // watchPosition is cancellable, unlike getCurrentPosition. It preserves the
  // browser's native permission prompt while guaranteeing Submit cannot wait
  // forever if the operating system does not return a coordinate.
  const position = await readBrowserPosition();
  const reporterPosition = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: position.coords.accuracy,
    captured_at: new Date().toISOString(),
  };
  cachePosition(reporterPosition);
  return reporterPosition;
};
